import type { TemplatePortfolioDraft } from "@adpilot/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { getApiErrorCode, getApiErrorMessage } from "@/lib/api-error";
import { templatePortfolioKeys, useUpdateTemplatePortfolioDraft } from "../api";
import { applyKeywordEdits, type KeywordEdit } from "./model";

const SAVE_DELAY_MS = 600;
const MAX_ATTEMPTS = 3;

/**
 * Keyword edits are queued locally and autosaved (PATCH keywords). Because AI tasks and
 * imports also write the draft, each save replays the queue on the latest server
 * keywords and retries on a version conflict instead of overwriting them.
 */
export function useKeywordEdits(shopId: string, draft: TemplatePortfolioDraft) {
  const queryClient = useQueryClient();
  const { mutateAsync: saveKeywords } = useUpdateTemplatePortfolioDraft(shopId, draft.id);
  const [edits, setEdits] = useState<KeywordEdit[]>([]);
  const editsRef = useRef(edits);
  editsRef.current = edits;
  const saving = useRef<Promise<void> | null>(null);

  const keywords = useMemo(() => applyKeywordEdits(draft.keywords, edits), [draft.keywords, edits]);

  const draftRef = useRef(draft);
  draftRef.current = draft;
  const latestDraft = useCallback(
    () =>
      queryClient.getQueryData<TemplatePortfolioDraft>(templatePortfolioKeys.draft(draft.id)) ??
      draftRef.current,
    [queryClient, draft.id],
  );

  const saveOnce = useCallback(async () => {
    const batch = editsRef.current;
    if (batch.length === 0) return;
    for (let attempt = 1; ; attempt += 1) {
      const current = latestDraft();
      try {
        await saveKeywords({
          expectedVersion: current.version,
          keywords: applyKeywordEdits(current.keywords, batch),
        });
        break;
      } catch (error) {
        const conflict =
          (await getApiErrorCode(error)) === "template_portfolio.draft.version_conflict";
        if (!conflict || attempt >= MAX_ATTEMPTS) throw error;
        await queryClient.refetchQueries({
          queryKey: templatePortfolioKeys.draft(draft.id),
          exact: true,
        });
      }
    }
    // Edits are only ever appended, so the saved ones are the prefix.
    setEdits((current) => current.slice(batch.length));
    editsRef.current = editsRef.current.slice(batch.length);
  }, [draft.id, latestDraft, queryClient, saveKeywords]);

  /** Saves every queued edit; resolves once the server has them (or rejects). */
  const flush = useCallback(async () => {
    while (saving.current) await saving.current.catch(() => undefined);
    if (editsRef.current.length === 0) return;
    const run = saveOnce();
    saving.current = run;
    try {
      await run;
    } finally {
      saving.current = null;
    }
  }, [saveOnce]);

  useEffect(() => {
    if (edits.length === 0) return;
    const timer = setTimeout(() => {
      flush().catch(async (error) => toast.error(await getApiErrorMessage(error)));
    }, SAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [edits, flush]);

  const edit = useCallback((next: KeywordEdit) => setEdits((current) => [...current, next]), []);

  return { keywords, edit, flush, latestDraft };
}
